import {
  ResolvedTadTimeEndpoint,
  TadTimeRangeWarning,
  TadTimeZone,
  formatTadInstant,
} from "@/domain/TadTimeRange";

export { formatTadInstant };

/**
 * TADS参照シートで「いま表示している窓」の状態と、その表示整形。
 * 状態を作るのは `SheetRowsIOWSLDSHost` (wslds.ts)、表示するのは `TadWindowBanner` と `TadTimeRangeForm`。
 */

/** 画面に出す「いま表示している窓」の状態。 */
export type TadWindowState = {
  startsAt: ResolvedTadTimeEndpoint | null;
  endsAt: ResolvedTadTimeEndpoint | null;
  timezone: TadTimeZone;
  limit: number;
  /** 窓を解決した基準時刻 (epoch ms)。相対指定は開くたびに変わるので必ず出す */
  evaluatedAt: number;
  /** 窓が次に動く時刻。null は「動かない」 */
  nextBoundaryAt: number | null;
  warnings: TadTimeRangeWarning[];
  /** limit で打ち切られた可能性 (サーバは打ち切りフラグを返さないので推定値) */
  isTruncated: boolean;
  /** 時間範囲が壊れていて解決できなかったときの理由 */
  resolveError: string | null;
  /** listen が拒否されたときの理由 */
  rejectedReason: string | null;
};

/** 解決済みの窓を「2026-08-01 00:00:00 以降 〜 2026-09-01 00:00:00 より前 (JST)」の形にする。 */
export const formatTadWindow = ({
  startsAt,
  endsAt,
  timezone,
}: Pick<TadWindowState, "startsAt" | "endsAt" | "timezone">): string => {
  const start =
    startsAt === null
      ? "下限なし"
      : `${formatTadInstant(startsAt.time, timezone)} ${
          startsAt.isExclusive ? "より後" : "以降"
        }`;
  const end =
    endsAt === null
      ? "上限なし"
      : `${formatTadInstant(endsAt.time, timezone)} ${
          endsAt.isExclusive ? "より前" : "以前"
        }`;
  return `${start} 〜 ${end} (${timezone})`;
};

/** ADR が UI への表示を要求している警告の文言。 */
export const tadTimeRangeWarningMessages: Record<TadTimeRangeWarning, string> =
  {
    "origin-component-truncated":
      "原点で指定されている成分がオフセット側で省略されているため、切り捨てられています",
    "start-after-end": "現在時刻で評価すると、開始が終了より後になっています",
    "may-become-start-after-end":
      "時間の経過によって、開始が終了より後になる可能性があります",
  };
